import { supabase } from '@/lib/supabaseClient';

// =====================================================
// INTERFACES
// =====================================================

export interface UserNotification {
  id: string;
  created_at: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  link_url: string | null;
  read_at: string | null;
  meta: Record<string, any> | null;
}

// =====================================================
// FUNCIONES PARA USUARIOS
// =====================================================

/**
 * Obtener notificaciones del usuario actual
 */
export async function getMyNotifications(): Promise<UserNotification[]> {
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase
    .from('user_notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return data as UserNotification[];
}

/**
 * Obtener solo notificaciones no leídas
 */
export async function getUnreadNotifications(): Promise<UserNotification[]> {
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase
    .from('user_notifications')
    .select('*')
    .is('read_at', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as UserNotification[];
}

/**
 * Contar notificaciones no leídas
 */
export async function getUnreadCount(): Promise<number> {
  if (!supabase) throw new Error('Supabase not configured');

  const { count, error } = await supabase
    .from('user_notifications')
    .select('*', { count: 'exact', head: true })
    .is('read_at', null);

  if (error) throw error;
  return count || 0;
}

/**
 * Marcar una notificación como leída
 */
export async function markNotificationRead(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase not configured');

  const { error } = await supabase
    .from('user_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
}

/**
 * Marcar todas las notificaciones como leídas
 */
export async function markAllNotificationsRead(): Promise<void> {
  if (!supabase) throw new Error('Supabase not configured');

  const { error } = await supabase
    .from('user_notifications')
    .update({ read_at: new Date().toISOString() })
    .is('read_at', null);

  if (error) throw error;
}

/**
 * Crear una notificación para el usuario actual
 */
export async function createNotification(payload: {
  type: string;
  title: string;
  message: string;
  link_url?: string;
  meta?: Record<string, any>;
}): Promise<UserNotification> {
  if (!supabase) throw new Error('Supabase not configured');

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('user_notifications')
    .insert({
      user_id: user.id,
      type: payload.type,
      title: payload.title,
      message: payload.message,
      link_url: payload.link_url || null,
      meta: payload.meta || null,
    })
    .select('*')
    .single();

  if (error) throw error;
  return data as UserNotification;
}

/**
 * Verificar si ya existe una notificación similar reciente (últimas 24h)
 */
export async function hasRecentNotification(
  type: string,
  hoursAgo: number = 24
): Promise<boolean> {
  if (!supabase) throw new Error('Supabase not configured');

  const cutoffDate = new Date();
  cutoffDate.setHours(cutoffDate.getHours() - hoursAgo);

  const { data, error } = await supabase
    .from('user_notifications')
    .select('id')
    .eq('type', type)
    .gte('created_at', cutoffDate.toISOString())
    .limit(1);

  if (error) throw error;
  return (data?.length || 0) > 0;
}

// =====================================================
// FUNCIONES PARA SUPERADMIN
// =====================================================

/**
 * Crear notificación para un usuario específico (SuperAdmin)
 */
export async function createNotificationForUser(
  userId: string,
  payload: {
    type: string;
    title: string;
    message: string;
    link_url?: string;
    meta?: Record<string, any>;
  }
): Promise<UserNotification> {
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase
    .from('user_notifications')
    .insert({
      user_id: userId,
      type: payload.type,
      title: payload.title,
      message: payload.message,
      link_url: payload.link_url || null,
      meta: payload.meta || null,
    })
    .select('*')
    .single();

  if (error) throw error;
  return data as UserNotification;
}

/**
 * Crear notificación para múltiples usuarios (SuperAdmin)
 */
export async function createBulkNotifications(
  userIds: string[],
  payload: {
    type: string;
    title: string;
    message: string;
    link_url?: string;
    meta?: Record<string, any>;
  }
): Promise<void> {
  if (!supabase) throw new Error('Supabase not configured');

  const notifications = userIds.map(userId => ({
    user_id: userId,
    type: payload.type,
    title: payload.title,
    message: payload.message,
    link_url: payload.link_url || null,
    meta: payload.meta || null,
  }));

  const { error } = await supabase
    .from('user_notifications')
    .insert(notifications);

  if (error) throw error;
}

/**
 * Obtener todas las notificaciones (SuperAdmin)
 */
export async function getAllNotificationsAdmin(): Promise<UserNotification[]> {
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase
    .from('user_notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(200);

  if (error) throw error;
  return data as UserNotification[];
}

// =====================================================
// HELPERS PARA NOTIFICACIONES AUTOMÁTICAS
// =====================================================

/**
 * Crear notificación de plan por vencer
 */
export async function notifyPlanExpiring(daysLeft: number, planEndDate: string): Promise<void> {
  const hasRecent = await hasRecentNotification('plan_expiring', 24);
  if (hasRecent) return;

  await createNotification({
    type: 'plan_expiring',
    title: 'Tu plan está por vencer',
    message: `Tu plan vence en ${daysLeft} días (${new Date(planEndDate).toLocaleDateString('es-ES')})`,
    link_url: '/app/billing',
    meta: { days_left: daysLeft, plan_end_date: planEndDate },
  });
}

/**
 * Crear notificación de almacenamiento bajo
 */
export async function notifyStorageWarning(usedPercent: number): Promise<void> {
  const hasRecent = await hasRecentNotification('storage_warning', 48);
  if (hasRecent) return;

  await createNotification({
    type: 'storage_warning',
    title: 'Espacio de almacenamiento bajo',
    message: `Has usado el ${usedPercent}% de tu almacenamiento disponible`,
    link_url: '/app/billing',
    meta: { used_percent: usedPercent },
  });
}

/**
 * Crear notificación de usuario inactivo
 */
export async function notifyInactiveUser(daysInactive: number): Promise<void> {
  const hasRecent = await hasRecentNotification('inactive_user', 168); // 7 días
  if (hasRecent) return;

  await createNotification({
    type: 'inactive_user',
    title: '¡Te extrañamos!',
    message: `Llevas ${daysInactive} días sin usar la plataforma. ¿Necesitas ayuda?`,
    link_url: '/app/dashboard',
    meta: { days_inactive: daysInactive },
  });
}
